import type { OnDidCommandEv } from "@core-ui/ApiServices/FetchService";
import ApiUrlCreatorService from "@core-ui/ContextServices/ApiUrlCreator";
import GitIndexService, { INDEX_CHANGING_COMMANDS } from "@core-ui/ContextServices/GitIndexService";
import PageDataContextService from "@core-ui/ContextServices/PageDataContext";
import WorkspaceService from "@core-ui/ContextServices/Workspace";
import { useApiEvent } from "@core-ui/hooks/useApi";
import { usePlatform } from "@core-ui/hooks/usePlatform";
import { useCallback, useEffect, useLayoutEffect, useRef } from "react";
import type { WorkspaceToolsState } from "../../model/types";
import { useWorkspaceToolsActions } from "../store/useWorkspaceToolsActions";
import { useWorkspaceToolsStore } from "../store/useWorkspaceToolsStore";
import { showFailedTools, showLandedTools } from "./showLandedTools";

// A reload clones the tools catalog again, and the changes it drops leave the Publish count with it.
GitIndexService.addIndexChangingCommand("workspaceTools/reload");

export const STATE_CHANGING_COMMANDS: ReadonlySet<string> = new Set([
	...INDEX_CHANGING_COMMANDS,
	"storage/getAllSyncCount",
	"storage/fetch",
]);

const REFRESH_DEBOUNCE_MS = 600;

const isLoading = (state: WorkspaceToolsState) =>
	state.enabled && !state.noSource && (state.status === "absent" || state.status === "cloning");

const willNotLoad = (state: WorkspaceToolsState) => state.enabled && (state.status === "error" || !!state.noSource);

const isPending = (state: WorkspaceToolsState) => state.enabled && state.status !== "ready";

export const becameReady = (before: WorkspaceToolsState, after: WorkspaceToolsState) =>
	isPending(before) && after.enabled && after.status === "ready";

export const becameFailed = (before: WorkspaceToolsState, after: WorkspaceToolsState) =>
	isLoading(before) && willNotLoad(after);

const useShowWhenReady = () => {
	const apiUrlCreator = ApiUrlCreatorService.value;
	const apiUrlCreatorRef = useRef(apiUrlCreator);
	apiUrlCreatorRef.current = apiUrlCreator;

	return useCallback(async (run: () => Promise<WorkspaceToolsState | null>) => {
		const before = useWorkspaceToolsStore.getState().state;
		const after = await run();
		if (after && becameReady(before, after)) await showLandedTools(apiUrlCreatorRef.current);
		else if (after && becameFailed(before, after)) await showFailedTools(apiUrlCreatorRef.current);
		return after;
	}, []);
};

export const useWorkspaceToolsSync = () => {
	const { isWeb, isTauri, isStatic, isStaticCli } = usePlatform();
	const workspace = WorkspaceService.current();
	const isLogged = PageDataContextService.value.isLogged;
	const { refresh, ensure } = useWorkspaceToolsActions();
	const showWhenReady = useShowWhenReady();
	const hasCommands = !isStatic && !isStaticCli;
	const clonesOnClient = isWeb || isTauri;

	const workspacePath = workspace?.path ?? null;
	useLayoutEffect(() => useWorkspaceToolsStore.getState().switchWorkspace(workspacePath), [workspacePath]);

	// biome-ignore lint/correctness/useExhaustiveDependencies: synced again only when the workspace, its tools path or the login change
	useEffect(() => {
		if (!hasCommands) return;
		void (async () => {
			const state = await showWhenReady(refresh);
			if (clonesOnClient && state?.enabled && !state.onDisk) await showWhenReady(() => ensure(true, !isLogged));
		})();
	}, [workspacePath, workspace?.toolsRepository, isLogged, hasCommands]);

	useDropErrorsWhileLoggedOut(isLogged);

	const refreshTimer = useRef<ReturnType<typeof setTimeout>>(null);
	useEffect(() => () => clearTimeout(refreshTimer.current), []);

	const onDidCommand = useCallback(
		({ command }: OnDidCommandEv) => {
			if (!STATE_CHANGING_COMMANDS.has(command) || !useWorkspaceToolsStore.getState().state.enabled) return;
			clearTimeout(refreshTimer.current);
			refreshTimer.current = setTimeout(() => void showWhenReady(refresh), REFRESH_DEBOUNCE_MS);
		},
		[refresh, showWhenReady],
	);
	useApiEvent("on-did-command", onDidCommand);
};

const useDropErrorsWhileLoggedOut = (isLogged: boolean) => {
	const errorSeq = useWorkspaceToolsStore((store) => (store.state.enabled ? store.state.error?.seq : undefined));
	const dismissError = useWorkspaceToolsStore((store) => store.dismissError);

	useEffect(() => {
		if (!isLogged && errorSeq !== undefined) dismissError(errorSeq);
	}, [isLogged, errorSeq, dismissError]);
};
